import wait from 'wait-promise';
import reportError from 'ha/helpers/bugsnag/reportError';
import { setCookie } from 'ha/modules/Cookies';
import getUtmSource from '../helpers/getUtmSource';
import getCampaignParams from '../helpers/getCampaignParams';
import trackGclid from './trackGclid';
import trackHomeToGo from './trackHomeToGo';
import trackNestpick from './trackNestpick';
import trackFbclid from './trackFbclid';
import identifyUser from './identifyUser';
import setRemarketingTag from './setRemarketingTag';
import track from './track';

const setupUTM = (mixpanel, location, referrer) => {
  const source = getUtmSource(location, referrer);

  mixpanel.register({ CurrentSource: source });
  mixpanel.register_once({ Source: source });
  mixpanel.people.set_once({ Source: source });
};

const setupCampaings = (mixpanel, location) => {
  // https://trello.com/c/ieIX3tTP/422-1-track-last-touch-attribution-via-utm-1
  // https://blog.mixpanel.com/2015/05/11/community-tip-last-touch-utm-tags/
  // Last Touch UTM Tags
  const campaignParams = getCampaignParams(location.query);

  mixpanel.people.set(campaignParams.params);
  mixpanel.people.set_once(campaignParams.firstParams);
  mixpanel.register(campaignParams.params);
};

const runTrackers = (dispatch, location) => {
  dispatch(trackHomeToGo(location.query));
  dispatch(trackNestpick(location.query));
  dispatch(trackGclid(location.query.gclid));
  dispatch(trackFbclid(location.query.fbclid));
};

const onIdentifiedUser = ({
  dispatch,
  mixpanel,
  category,
  hideIntercom,
  name,
  remarketingTag,
  traits,
}) => {
  // https://mixpanel.com/blog/2015/01/06/community-tip-tracking-first-time-users
  // Register a qsuper-property to track "First time" users.
  // It will be stored in cookies, and it doesn't require any HTTP requests to MixPanel.
  // All next events of the user will include this super-property.
  dispatch(
    track('page', {
      traits: {
        name,
        category,
        ...traits,
      },
      options: {
        Intercom: {
          hideDefaultLauncher: hideIntercom,
        },
      },
    }),
  );

  if (mixpanel) {
    mixpanel.register({ 'First Time': 'FALSE' });
  }

  if (remarketingTag) {
    dispatch(setRemarketingTag(remarketingTag));
  }
};

const setupAjsDistinctId = (dispatch, mixpanel, config) => {
  wait
    .before(60000)
    .until(() => mixpanel.__loaded) // eslint-disable-line no-underscore-dangle
    .then(() => {
      // Card #3094. A part of proof-of-concept. Must be generalized in case of validness.
      // Supplying registration events with Mixpanel's distinct_id.
      dispatch(setCookie('ajs_distinct_id', mixpanel.get_distinct_id()));
    })
    .catch(() => {
      if (config.debug) {
        reportError({
          name: 'Mixpanel',
          message: 'Loading failed by timeout',
        });
      }
    });
};

const setupGtag = config => {
  if (global.gtag && config.googleTrackingId) {
    global.gtag('config', config.googleTrackingId, {
      send_page_view: false,
    });
  }
};

export default ({
  config,
  category,
  hideIntercom,
  location,
  name,
  referrer,
  remarketingTag,
  traits,
}) => (dispatch, _, { segment }) =>
  segment.onReady(mixpanel => {
    runTrackers(dispatch, location);
    setupGtag(config);

    if (mixpanel) {
      setupUTM(mixpanel, location, referrer);
      setupCampaings(mixpanel, location);
      setupAjsDistinctId(dispatch, mixpanel, config);
    } else if (config.debug) {
      reportError({ name: 'Mixpanel', message: 'Is not defined' });
    }

    dispatch(identifyUser());

    onIdentifiedUser({
      dispatch,
      mixpanel,
      category,
      hideIntercom,
      name,
      remarketingTag,
      traits,
    });
  });
